import type React from 'react';

import type { TranscriptCue } from '../player/player.interface';

import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import { removeHtmlFromText } from '../../utils/html';
import { TimeBadge } from '../time-badge/time-badge';

import styles from './transcript-item.module.css';

type TranscriptItemProps = React.ComponentProps<'button'> & {
  cue: TranscriptCue;
  isActive?: boolean;
  onItemClick?: (cue: TranscriptCue) => void;
  search?: string;
  activeHighlightedIndex?: number;
};

export function TranscriptItem({
  cue,
  isActive,
  onItemClick,
  search,
  activeHighlightedIndex,
  ...props
}: TranscriptItemProps) {
  const handleOnClick = useCallback(() => {
    onItemClick?.(cue);
  }, [onItemClick, cue]);

  const sanitizedText = useMemo(() => removeHtmlFromText(cue.text), [cue.text]);

  const highlightedText = useMemo(
    () => getHighlightedText(sanitizedText, search, activeHighlightedIndex),
    [sanitizedText, search, activeHighlightedIndex]
  );

  return (
    <button {...props} type="button" role="menuitem" onClick={handleOnClick} className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.title}>{highlightedText}</div>
        <TimeBadge time={cue.startTime} isActive={isActive} />
      </div>
    </button>
  );
}

const getHighlightedText = (text: string, highlight?: string, activeHighlightIndex?: number): React.ReactNode => {
  if (!highlight) {
    return text;
  }
  // Split on highlight term and include term into parts, ignore case
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));

  if (parts.length === 1) {
    return text;
  }

  let matchIndex = 0;
  return (
    <>
      {parts.map((part, i) => {
        const isHighlighted = part.toLowerCase() === highlight.toLowerCase();
        const isActiveHighlight = isHighlighted && activeHighlightIndex === matchIndex;
        if (isHighlighted) {
          matchIndex++;
        }
        return part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className={classNames(styles.highlightedText, isActiveHighlight && styles.active)}>
            {part}
          </span>
        ) : (
          part
        );
      })}
    </>
  );
};
